import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag, Briefcase, Gift, Diamond, Check } from "lucide-react";
import { setPageMeta, JsonLd, servicePriceSchema, breadcrumbSchema } from "../lib/schema";
import PageHeader from "../components/layout/PageHeader";

const SERVICES = [
  {
    icon: ShoppingBag,
    title: "Personal Shopping",
    blurb:
      "For individuals. Anything sold in the UK — fashion, electronics, beauty, sportswear, baby goods — sourced and shipped to your door.",
    bullets: [
      "Any UK retailer, online or in-store",
      "Consolidated shipping (multiple orders, one freight bill)",
      "Photographs at every stage",
      "From £20 service fee",
    ],
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    icon: Briefcase,
    title: "Business Procurement (B2B)",
    blurb:
      "For Nigerian businesses sourcing UK products at scale. Office equipment, branded merchandise, consumables, tech.",
    bullets: [
      "Bulk pricing & monthly retainers",
      "Custom invoicing & purchase orders",
      "Branded merchandise sourcing",
      "Volume freight rates",
    ],
    image:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    icon: Gift,
    title: "Gift Sourcing",
    blurb:
      "Curated gifts sourced and delivered direct to your loved ones in Nigeria — anonymously, discreetly, with no invoice or pricing visible.",
    bullets: [
      "Direct delivery to recipient",
      "Discreet plain-packaging option",
      "Gift cards & handwritten notes",
      "Surprise-friendly tracking",
    ],
    image:
      "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    icon: Diamond,
    title: "Luxury & Designer",
    blurb:
      "Sourced only from authorised UK retailers and brand boutiques. Authenticated in-store, photographed at every stage, fully insured.",
    bullets: [
      "Brand boutiques + authorised retailers only",
      "In-store verification & photographs",
      "Full insurance on every parcel",
      "Discreet packaging on request",
    ],
    image:
      "https://images.unsplash.com/photo-1584917865442-de89df76afd3?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
];

export default function ServicesPage() {
  useEffect(() => {
    setPageMeta({
      title: "UK Shopping Services for Nigerians | My Private Shopper",
      description:
        "Four MPS service tiers — Personal Shopping, Business Procurement, Gift Sourcing, and Luxury & Designer — all sourced in the UK and delivered to Nigeria.",
      canonical: "https://privateshopper.co.uk/services",
    });
  }, []);

  return (
    <>
      <JsonLd data={servicePriceSchema} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services" },
      ])} />
      <PageHeader
        overline="What We Do"
        title={<>Four ways we shop <span className="italic text-[#C9A96E]">for you</span>.</>}
        body="Every order is handled by a real human concierge in London — never an automated forwarder, never a marketplace middleman."
      />

      <section className="container-mps pb-24 md:pb-32">
        <div className="space-y-24">
          {SERVICES.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.div
                key={s.title}
                data-testid={`services-detail-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
              >
                <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                  <img src={s.image} alt={s.title} className="w-full h-[460px] object-cover" />
                </div>
                <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                  <s.icon className="text-[#C9A96E] mb-5" size={28} strokeWidth={1.4} />
                  <h2 className="font-serif text-3xl md:text-5xl tracking-tight">{s.title}</h2>
                  <div className="gold-divider mt-6" />
                  <p className="mt-8 text-[#6B7280] leading-relaxed text-lg">{s.blurb}</p>
                  <ul className="mt-8 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#1A1A1A]">
                        <Check className="text-[#C9A96E] mt-1 shrink-0" size={18} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/order"
                    className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.18em] hover:bg-[#1A1A1A]"
                  >
                    Start Your Order <ArrowUpRight size={14} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
}
